// --- START OF FILE apps/reseye-web/src/utils/NotificationManager.js ---
import { openDB } from 'idb';
import { messaging, functions, db } from '../firebase';
import { getToken, onMessage } from 'firebase/messaging';
import { httpsCallable } from 'firebase/functions';
import { collection, query, where, getDocs } from 'firebase/firestore';

console.log('RESEYE NotificationManager v1.0.13 initialized');

const DB_NAME = 'ReseyeLogs';
const STORE_NAME = 'notifications';
const IDENTITY_STORE = 'identity';
const VAPID_KEY = 'BCIEK-LLPXMyNolFUC7-DWWmRURLpQDjKm0tnxMlMa8lh1FDaEPcjR8dxHVHs6SR_slfs0x0p-wK9K6rxgqm3yk';

class NotificationManager {
    constructor() {
        this.dbPromise = openDB(DB_NAME, 1, {
            upgrade(db) {
                if (!db.objectStoreNames.contains(STORE_NAME)) {
                    db.createObjectStore(STORE_NAME, { keyPath: 'id', autoIncrement: true });
                }
                if (!db.objectStoreNames.contains(IDENTITY_STORE)) {
                    db.createObjectStore(IDENTITY_STORE);
                }
            },
        });
        this.alarmAudio = null;
        this.isAudioUnlocked = false;
    }

    async unlockAudio() {
        if (this.isAudioUnlocked) return;

        // Usar un silencio base64 de 1ms para evitar fallos de carga y sonidos fantasma.
        const silence = 'data:audio/wav;base64,UklGRigAAABXQVZFRm10IBAAAAABAAEARKwAAIhYAQACABAAZGF0YQQAAAAAAAAl';
        const unlocker = new Audio(silence);

        try {
            await unlocker.play();
        } catch (e) {
            // Ignorar fallos si aún no hay interacción
        } finally {
            this.isAudioUnlocked = true; // Forzar a true para que deje de intentarlo en cada clic
        }
    }

    async getDeviceUUID() {
        const db = await this.dbPromise;
        let uuid = await db.get(IDENTITY_STORE, 'deviceUUID');
        if (!uuid) {
            uuid = crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).substring(2) + Date.now().toString(36);
            await db.put(IDENTITY_STORE, uuid, 'deviceUUID');
        }
        return uuid;
    }

    getDeviceType() {
        const ua = navigator.userAgent;
        if (/(tablet|ipad|playbook|silk)|(android(?!.*mobi))/i.test(ua)) {
            return 'tablet';
        }
        if (/Mobile|Android|iP(hone|od)|IEMobile|BlackBerry|Kindle|Silk-Accelerated|(hpw|web)OS|Opera M(obi|ini)/.test(ua)) {
            return 'mobile';
        }
        return 'desktop';
    }

    async logNotification(payload) {
        const db = await this.dbPromise;
        await db.add(STORE_NAME, {
            ...payload,
            timestamp: new Date().toISOString()
        });
    }

    async getLogs() {
        const db = await this.dbPromise;
        return db.getAll(STORE_NAME);
    }

    async registerServiceWorker() {
        if ('serviceWorker' in navigator) {
            try {
                // Si ya hay un SW listo, no registrar de nuevo innecesariamente
                const registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js');
                console.log('✅ Service Worker registered successfully.');
                console.log('   - Scope:', registration.scope);
                return registration;
            } catch (error) {
                console.error('Service Worker registration failed:', error);
                return null;
            }
        }
        return null;
    }

    async registerToken() {
        console.log('--- FCM Registration Diagnostic ---');
        console.log('1. Checking notification permission:', Notification.permission);

        try {
            const permission = await Notification.requestPermission();
            if (permission !== 'granted') {
                throw new Error(`Permiso de notificaciones denegado: ${permission}`);
            }

            console.log('2. Ensuring SW registration...');
            const registration = await this.registerServiceWorker();
            if (!registration) throw new Error('Failed to register Service Worker');
            console.log('3. SW Ready (v1.0.13):', registration.active?.state || 'waiting');

            console.log('4. Requesting FCM Token...');
            const token = await getToken(messaging, {
                vapidKey: VAPID_KEY,
                serviceWorkerRegistration: registration
            });

            if (!token) {
                throw new Error('No se pudo generar el token (getToken devolvió vacío).');
            }

            console.log('5. Token Generated (FULL):', token);

            const uuid = await this.getDeviceUUID();
            console.log('6. Device UUID:', uuid);

            const registerFcmToken = httpsCallable(functions, 'registerFcmToken');

            console.log('7. Sending to Cloud Functions...');
            const result = await registerFcmToken({
                token,
                deviceType: this.getDeviceType(),
                deviceUUID: uuid
            });

            console.log('✅ FCM Registration SUCCESS:', result.data);
            return token;
        } catch (error) {
            console.error('❌ FCM Registration FAILED:', error);
            throw error;
        }
    }

    async unregisterToken(token) {
        if (!token) return;
        try {
            const unregisterFcmToken = httpsCallable(functions, 'unregisterFcmToken');
            await unregisterFcmToken({ token });
        } catch (error) {
            console.error('Error unregistering FCM token:', error);
        }
    }

    initForegroundListener() {
        console.log('📡 Foreground FCM listener initialized');
        onMessage(messaging, (payload) => {
            console.log('🔔 NEW FCM MESSAGE RECEIVED:', payload);

            // Permitir que funcione con payload.notification (legacy) o payload.data (nuestro nuevo formato)
            const data = payload.notification || payload.data;

            if (data) {
                console.log('   - Title:', data.title);
                this.logNotification(data);
                this.playAlarm();
            } else {
                console.log('   - (Message had no notification or data payload)');
            }

            if ('vibrate' in navigator) {
                navigator.vibrate([200, 100, 200, 100, 200]);
            }

            if (this.onMessageCallback) {
                // NORMALIZACIÓN: Inyectamos data en notification para que la UI de React 
                // siga funcionando sin cambios ante el nuevo formato data-only.
                const normalizedPayload = {
                    ...payload,
                    notification: payload.notification || payload.data
                };
                this.onMessageCallback(normalizedPayload);
            }
        });
    }

    playAlarm() {
        this.isAlarmActive = true; // Control de intención de sonido

        if (!this.alarmAudio) {
            this.alarmAudio = new Audio('/alarm.mp3');
            this.alarmAudio.loop = true;
        }

        this.alarmAudio.play().catch(() => {
            const playOnInteract = () => {
                // Solo sonar si la alarma sigue activa (no se ha pulsado cerrar)
                if (this.isAlarmActive && this.alarmAudio) {
                    this.alarmAudio.play().catch(() => { });
                }
                document.removeEventListener('click', playOnInteract);
            };
            document.addEventListener('click', playOnInteract);
        });
    }

    stopAlarm() {
        this.isAlarmActive = false; // Detener flag de intención
        if (this.alarmAudio) {
            this.alarmAudio.pause();
            this.alarmAudio.currentTime = 0;
            this.alarmAudio.loop = false; // Asegurar que no re-active el loop
        }
    }

    async checkSessionConflict(uid) {
        if (!uid) return null;
        try {
            const currentUUID = await this.getDeviceUUID();
            const deviceType = this.getDeviceType();

            const tokensRef = collection(db, `users/${uid}/activeTokens`);
            const q = query(tokensRef, where("deviceType", "==", deviceType), where("activo", "==", true));
            const snapshot = await getDocs(q);

            if (snapshot.empty) return 'inactive';

            const activeTokenDoc = snapshot.docs[0].data();

            if (activeTokenDoc.deviceUUID !== currentUUID) {
                return 'conflict';
            }
            return 'active';
        } catch (error) {
            console.error('Error checking session conflict:', error);
            return null;
        }
    }

    setOnMessageListener(callback) {
        this.onMessageCallback = callback;
    }
}

export const notificationManager = new NotificationManager();
// --- END OF FILE apps/reseye-web/src/utils/NotificationManager.js ---
